"use client";

import DezuTransparent from "../../../../public/logos/dezu-icon-black.png";
import Image from "next/image";
import { useEffect, useState, Suspense, FormEvent } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import type { Container, Engine } from "@tsparticles/engine";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";
import { Icons } from "@/components/ui/icons";
import { useSignIn } from "@clerk/nextjs";
import { useRouter } from "next/navigation";

type LoginStep = "email" | "password" | "forgot" | "reset-code" | "success";

export default function LoginPage() {
  const { signIn, fetchStatus } = useSignIn();
  const router = useRouter();

  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Particles state
  const [init, setInit] = useState(false);

  const isLoading = loading || fetchStatus === "fetching";

  useEffect(() => {
    document.title = "Login | Dezu";
  }, []);

  useEffect(() => {
    initParticlesEngine(async (engine: Engine) => {
      await loadSlim(engine);
    })
      .then(() => {
        setInit(true);
      })
      .catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.error("Particles initialization failed:", err);
        }
        setInit(true);
      });
  }, []);

  const particlesLoaded = async (container?: Container) => {
    if (process.env.NODE_ENV === "development") {
      console.log("Particles container loaded", container);
    }
  };

  function formatError(err: unknown): string {
    if (err && typeof err === "object" && "errors" in err) {
      const clerkErr = err as {
        errors: Array<{ longMessage?: string; message?: string }>;
      };
      return (
        clerkErr.errors[0]?.longMessage ||
        clerkErr.errors[0]?.message ||
        "An error occurred"
      );
    }
    if (err instanceof Error) return err.message;
    return "An unexpected error occurred";
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    if (!signIn) return;
    setLoading(true);
    setError("");
    try {
      const { error: err } = await signIn.create({ identifier: email });
      if (err) {
        setError(err.message || "An error occurred");
      } else {
        setStep("password");
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (!signIn) return;
    setLoading(true);
    setError("");
    try {
      const { error: err } = await signIn.password({ password });
      if (err) {
        setError(err.message || "Invalid password");
      } else if (signIn.status === "complete") {
        await signIn.finalize();
        router.push("/dashboard");
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!signIn) return;
    setLoading(true);
    setError("");
    try {
      const { error: err } = await signIn.resetPasswordEmailCode.sendCode();
      if (err) {
        setError(err.message || "Could not send reset code");
      } else {
        setStep("reset-code");
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleResetSubmit(e: FormEvent) {
    e.preventDefault();
    if (!signIn) return;
    setLoading(true);
    setError("");
    try {
      const { error: verifyErr } =
        await signIn.resetPasswordEmailCode.verifyCode({ code });
      if (verifyErr) {
        setError(verifyErr.message || "Invalid code");
        setLoading(false);
        return;
      }
      const { error: resetErr } =
        await signIn.resetPasswordEmailCode.submitPassword({
          password: newPassword,
        });
      if (resetErr) {
        setError(resetErr.message || "Could not reset password");
      } else {
        setStep("success");
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleContinueToDashboard(e: FormEvent) {
    e.preventDefault();
    if (!signIn) return;
    setLoading(true);
    try {
      await signIn.finalize();
      router.push("/dashboard");
    } catch (err) {
      setError(formatError(err));
      setLoading(false);
    }
  }

  if (!signIn) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Icons.spinner className="size-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left Side */}
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <a href="#" className="flex items-center gap-2 font-medium">
            <div className="flex h-8 w-8 items-center justify-center rounded-md text-primary-foreground">
              <Image
                src={DezuTransparent}
                alt="Dezu"
                width={32}
                height={32}
              />
            </div>
            Dezu
          </a>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            <Suspense
              fallback={
                <div className="flex items-center justify-center">
                  <Icons.spinner className="size-4 animate-spin" />
                </div>
              }
            >
              <AnimatePresence mode="wait">
                {/* Step: Email Entry */}
                {step === "email" && (
                  <motion.div
                    key="email"
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <form onSubmit={handleEmailSubmit}>
                      <div className={cn("flex flex-col gap-6")}>
                        <div className="flex flex-col items-center gap-2 text-center">
                          <h1 className="text-2xl font-bold">Welcome back!</h1>
                          <p className="text-balance text-sm text-muted-foreground">
                            Enter your email to continue
                          </p>
                        </div>
                        <div className="grid gap-6">
                          <div className="grid gap-2">
                            <div className="space-y-2">
                              <Label htmlFor="identifier">Email</Label>
                              <Input
                                id="identifier"
                                type="email"
                                placeholder="m@example.com"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                              />
                              {error && (
                                <p className="text-sm text-destructive">
                                  {error}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full"
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center gap-2">
                                <Icons.spinner className="size-4 animate-spin" />
                                <span>Continuing...</span>
                              </div>
                            ) : (
                              "Continue"
                            )}
                          </Button>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Step: Password Entry */}
                {step === "password" && (
                  <motion.div
                    key="password"
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <form onSubmit={handlePasswordSubmit}>
                      <div className="flex flex-col gap-6">
                        <div className="flex flex-col items-center gap-2 text-center">
                          <h1 className="text-2xl font-bold">
                            Enter your password
                          </h1>
                          <p className="text-balance text-sm text-muted-foreground">
                            Please enter your password to sign in
                          </p>
                        </div>
                        <div className="grid gap-6">
                          <div className="grid gap-2">
                            <div className="space-y-2">
                              <Label htmlFor="password">Password</Label>
                              <div className="relative">
                                <Input
                                  id="password"
                                  type={showPassword ? "text" : "password"}
                                  required
                                  className="pr-10"
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowPassword(!showPassword)}
                                  aria-label={
                                    showPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                >
                                  {showPassword ? (
                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </Button>
                              </div>
                              {error && (
                                <p className="text-sm text-destructive">
                                  {error}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full"
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center gap-2">
                                <Icons.spinner className="size-4 animate-spin" />
                                <span>Signing in...</span>
                              </div>
                            ) : (
                              "Sign in"
                            )}
                          </Button>
                          <div className="text-center">
                            <Button
                              type="button"
                              variant="link"
                              className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground"
                              onClick={handleForgotPassword}
                              disabled={isLoading}
                            >
                              Forgot your password?
                            </Button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Step: Reset Code + New Password */}
                {step === "reset-code" && (
                  <motion.div
                    key="reset-code"
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <form onSubmit={handleResetSubmit}>
                      <div className="flex flex-col gap-6">
                        <div className="flex flex-col items-center gap-2 text-center">
                          <h1 className="text-2xl font-bold">
                            Check your email
                          </h1>
                          <p className="text-balance text-sm text-muted-foreground">
                            Enter the code from your email and your new password
                          </p>
                        </div>
                        <div className="grid gap-6">
                          <div className="grid gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="reset-code">Reset code</Label>
                              <Input
                                id="reset-code"
                                required
                                placeholder="123456"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="new-password">New password</Label>
                              <div className="relative">
                                <Input
                                  id="new-password"
                                  type={showNewPassword ? "text" : "password"}
                                  required
                                  className="pr-10"
                                  value={newPassword}
                                  onChange={(e) =>
                                    setNewPassword(e.target.value)
                                  }
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() =>
                                    setShowNewPassword(!showNewPassword)
                                  }
                                  aria-label={
                                    showNewPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                >
                                  {showNewPassword ? (
                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </Button>
                              </div>
                              {error && (
                                <p className="text-sm text-destructive">
                                  {error}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full"
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center gap-2">
                                <Icons.spinner className="size-4 animate-spin" />
                                <span>Updating password...</span>
                              </div>
                            ) : (
                              "Update password"
                            )}
                          </Button>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Step: Success */}
                {step === "success" && (
                  <motion.div
                    key="success"
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <form onSubmit={handleContinueToDashboard}>
                      <div className="flex flex-col gap-6">
                        <div className="flex flex-col items-center gap-2 text-center">
                          <h1 className="text-2xl font-bold">
                            Password updated successfully!
                          </h1>
                          <p className="text-balance text-sm text-muted-foreground">
                            You&apos;re now signed in. Redirecting to
                            dashboard...
                          </p>
                        </div>
                        <div className="grid gap-6">
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full"
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center gap-2">
                                <Icons.spinner className="size-4 animate-spin" />
                                <span>Completing sign in...</span>
                              </div>
                            ) : (
                              "Continue to Dashboard"
                            )}
                          </Button>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </Suspense>
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div className="relative hidden lg:block bg-white h-full">
        {init && (
          <Particles
            id="tsparticles"
            particlesLoaded={particlesLoaded}
            className="absolute inset-0 w-full h-full"
            options={{
              fullScreen: {
                enable: false,
              },
              background: {
                color: {
                  value: "#171614",
                },
              },
              particles: {
                number: {
                  value: 250,
                  density: {
                    enable: true,
                  },
                },
                color: {
                  value: "#ffffff",
                },
                shape: {
                  type: "circle",
                },
                opacity: {
                  value: 0.5,
                },
                size: {
                  value: 0,
                },
                links: {
                  enable: true,
                  distance: 150,
                  color: "#ffffff",
                  opacity: 0.4,
                  width: 2,
                },
                move: {
                  enable: true,
                  speed: 2,
                  direction: "none",
                  random: true,
                  straight: false,
                  outModes: {
                    default: "out",
                  },
                  attract: {
                    enable: false,
                  },
                },
              },
              interactivity: {
                detectsOn: "parent",
                events: {
                  onHover: {
                    enable: true,
                    mode: "grab",
                  },
                  onClick: {
                    enable: true,
                    mode: "push",
                  },
                },
                modes: {
                  grab: {
                    distance: 150,
                    links: {
                      opacity: 1,
                    },
                  },
                  bubble: {
                    distance: 400,
                    size: 40,
                    duration: 2,
                    opacity: 8,
                    speed: 3,
                  },
                  repulse: {
                    distance: 200,
                    duration: 0.4,
                  },
                  push: {
                    quantity: 4,
                  },
                  remove: {
                    quantity: 2,
                  },
                },
              },
              detectRetina: true,
            }}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <h2 className="text-3xl md:text-3xl font-bold text-white text-center max-w-2xl leading-tight px-6">
            Save 90% of your time on complex tax returns with Dezu
          </h2>
        </div>
      </div>
    </div>
  );
}
